import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserState, User } from '../types';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

// Кэш для предотвращения частых запросов
let lastFetchTime = 0;
const CACHE_DURATION = 5000; // 5 секунд - не делать запросы чаще чем раз в 5 секунд
const STORAGE_CACHE_DURATION = 60000; // 1 минута - время жизни данных в localStorage

// Очистка устаревших данных из localStorage при загрузке
const clearStaleCache = () => {
  try {
    const storedData = localStorage.getItem('user-storage');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      const lastUpdate = parsed?.state?._lastUpdate;
      const now = Date.now();
      
      // Если данные старше STORAGE_CACHE_DURATION или нет timestamp - очищаем
      if (!lastUpdate || now - lastUpdate > STORAGE_CACHE_DURATION) {
        // Очищаем устаревший кэш (без лога - происходит автоматически)
        localStorage.removeItem('user-storage');
      }
    }
  } catch (error) {
    // В случае ошибки - очищаем кеш на всякий случай
    localStorage.removeItem('user-storage');
  }
};

// Очищаем устаревшие данные при загрузке модуля
clearStaleCache();

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,
      _lastUpdate: 0, // Timestamp последнего обновления

      setUser: (user: User) => 
        set({ user, error: null, _lastUpdate: Date.now() } as Partial<UserState>),

      clearUser: () => 
        set({ user: null, error: null, _lastUpdate: 0 } as Partial<UserState>),

      updateTokens: (tokens: number) =>
        set((state) => ({
          user: state.user ? { ...state.user, tokens } : null
        } as Partial<UserState>)),

      updateSubscription: (subscription: { tier: string; period?: string | null; status?: string }) =>
        set((state) => ({
          user: state.user ? {
            ...state.user,
            subscription: {
              ...(state.user.subscription || {}),
              ...subscription
            }
          } : null
        } as Partial<UserState>)),

      initUser: async () => {
        set({ isLoading: true, error: null });
        
        try {
          if (!hasAuthData()) {
            throw new Error('No authentication data available');
          }

          // Получаем start_param из Telegram WebApp (для рефералов и кампаний)
          const tg = (window as any).Telegram?.WebApp;
          const startParam = tg?.initDataUnsafe?.start_param || null;

          // Проверяем pending реферал из localStorage (для Google/веб-пользователей)
          let webReferral: string | null = null;
          try {
            const pendingReferral = localStorage.getItem('pending_referral');
            if (pendingReferral) {
              const parsed = JSON.parse(pendingReferral);
              if (parsed.code && Date.now() - parsed.timestamp < 30 * 24 * 60 * 60 * 1000) {
                webReferral = `ref_${parsed.code}`;
                localStorage.removeItem('pending_referral');
              } else {
                localStorage.removeItem('pending_referral');
              }
            }
          } catch {
            // Ignore parsing errors
          }

          // Также проверяем UTM из localStorage (для переходов с сайта)
          let utmCampaign: string | null = null;
          let websiteUtmData: Record<string, string> | null = null;
          try {
            const pendingCampaign = localStorage.getItem('pending_campaign');
            if (pendingCampaign) {
              const parsed = JSON.parse(pendingCampaign);
              if (parsed.campaign && Date.now() - parsed.timestamp < 30 * 24 * 60 * 60 * 1000) {
                // Prevent double c_ prefix: if campaign already starts with c_, use as-is
                utmCampaign = parsed.campaign.startsWith('c_')
                  ? parsed.campaign
                  : `c_${parsed.campaign}`;

                websiteUtmData = {
                  utm_campaign: parsed.campaign,
                  ...(parsed.source && { utm_source: parsed.source }),
                  ...(parsed.medium && { utm_medium: parsed.medium }),
                };
                localStorage.removeItem('pending_campaign');
              }
            }
          } catch {
            // Ignore parsing errors
          }

          // Определяем какой start_param использовать
          // Приоритет: Telegram start_param > web referral > UTM campaign
          const effectiveStartParam = startParam || webReferral || utmCampaign;
          const effectiveSourceType = startParam ? undefined : ((webReferral || utmCampaign) ? 'website' : undefined);

          // Вызываем /api/user/init для регистрации рефералов и кампаний
          const data = await apiClient.user.initUser(
            effectiveStartParam,
            effectiveSourceType,
            websiteUtmData
          );

          if (data.success && data.user) {
            const userWithSubscription = {
              ...data.user,
              subscription: data.subscription,
              settings: data.settings || {}
            };
            
            // Сохраняем пользователя в state!
            set({ 
              user: userWithSubscription, 
              isLoading: false, 
              error: null,
              _lastUpdate: Date.now()
            } as Partial<UserState>);
            
            return {
              user: userWithSubscription,
              referralRegistered: data.referralRegistered || false,
              referrerInfo: data.referrerInfo || null,
              campaignInfo: data.campaignInfo || null
            };
          } else {
            throw new Error(data.error || 'Failed to initialize user');
          }
        } catch (error: any) {
          // Обработка ACCESS_DENIED из axios error response
          const responseData = error.response?.data;
          if (responseData?.error === 'ACCESS_DENIED') {
            const accessError: any = new Error(responseData.message || 'У вас нет доступа');
            accessError.error = 'ACCESS_DENIED';
            accessError.details = responseData.details;
            set({
              isLoading: false,
              error: accessError.message
            });
            throw accessError;
          }
          set({
            isLoading: false,
            error: error.response?.data?.message || error.message || 'Failed to initialize user'
          });
          throw error;
        }
      },

      fetchUser: async (forceRefresh = false) => {
        // Проверяем кэш - если данные свежие и не форсируем обновление, возвращаем существующие
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTime;
        
        if (!forceRefresh && timeSinceLastFetch < CACHE_DURATION && get().user) {
          return get().user;
        }

        set({ isLoading: true, error: null });
        
        try {
          const data = await apiClient.user.getUserProfile(forceRefresh);

          if (data.success && data.user) {
            const userWithSubscription = {
              ...data.user,
              subscription: data.subscription,
              settings: data.settings || {}
            };
            
            lastFetchTime = now;
            
            set({ 
              user: userWithSubscription, 
              isLoading: false,
              error: null,
              _lastUpdate: Date.now()
            } as Partial<UserState>);
            
            return userWithSubscription;
          } else {
            throw new Error(data.error || 'Failed to fetch user');
          }
        } catch (error: any) {
          // Обработка ACCESS_DENIED из axios error response
          const responseData = error.response?.data;
          if (responseData?.error === 'ACCESS_DENIED') {
            const accessError: any = new Error(responseData.message || 'У вас нет доступа');
            accessError.error = 'ACCESS_DENIED';
            accessError.details = responseData.details;
            set({
              isLoading: false,
              error: accessError.message
            });
            throw accessError;
          }
          set({
            isLoading: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch user'
          });
          throw error;
        }
      },
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
);
